import { useState, useEffect, useMemo } from 'react';
import { Search, SlidersHorizontal, X, Flame } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { menuItems, categories, dietaryTags } from '../data/menuData';
import { ProductCard, ProductCardSkeleton } from '../components/menu/ProductCard';
import { ProductDetail } from '../components/menu/ProductDetail';
import { motion, AnimatePresence } from 'framer-motion';

export default function MenuPage() {
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('popular');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  
  // Filters state
  const [filters, setFilters] = useState({
    dietary: [],
    priceRange: [0, 300],
    spicy: false,
    popular: false,
  });

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => setLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);

  // Filter products
  const filteredProducts = useMemo(() => {
    let products = [...menuItems];

    // Filter by category
    if (activeCategory !== 'popular') {
      products = products.filter(p => p.category === activeCategory);
    } else {
      products = products.filter(p => p.isPopular);
    }

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      products = products.filter(p => 
        p.name.toLowerCase().includes(query) ||
        p.description.toLowerCase().includes(query)
      );
    }

    // Filter by dietary preferences
    if (filters.dietary.length > 0) {
      products = products.filter(p => 
        filters.dietary.every(diet => p.tags?.includes(diet))
      );
    }

    // Filter by price range
    products = products.filter(p => 
      p.price >= filters.priceRange[0] && p.price <= filters.priceRange[1]
    );

    // Filter by spicy
    if (filters.spicy) {
      products = products.filter(p => p.spiceLevel && p.spiceLevel > 0);
    }

    // Filter by popular
    if (filters.popular) {
      products = products.filter(p => p.isPopular);
    }

    return products;
  }, [activeCategory, searchQuery, filters]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.dietary.length > 0) count += filters.dietary.length;
    if (filters.spicy) count++;
    if (filters.popular) count++;
    if (filters.priceRange[0] > 0 || filters.priceRange[1] < 300) count++;
    return count;
  }, [filters]);

  const clearFilters = () => {
    setFilters({
      dietary: [],
      priceRange: [0, 300],
      spicy: false,
      popular: false,
    });
    setSearchQuery('');
  };

  const toggleDietaryFilter = (diet) => {
    setFilters(prev => ({
      ...prev,
      dietary: prev.dietary.includes(diet)
        ? prev.dietary.filter(d => d !== diet)
        : [...prev.dietary, diet]
    }));
  };

  return (
    <div className="min-h-screen pb-24 md:pb-12">
      {/* Header */}
      <div className="bg-secondary/30 border-b border-border/50">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8 md:py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tight">
              Nuestro Menú
            </h1>
            <p className="text-muted-foreground mt-2">
              Explora más de {menuItems.length} opciones deliciosas
            </p>
          </motion.div>

          {/* Search and Filters */}
          <div className="flex gap-3 mt-6">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar platillos..."
                className="pl-12 h-12 bg-background rounded-full border-border/60"
                data-testid="menu-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="outline" 
                  className="h-12 px-4 rounded-full relative"
                  data-testid="filters-button"
                >
                  <SlidersHorizontal className="w-5 h-5" />
                  <span className="hidden sm:inline ml-2">Filtros</span>
                  {activeFiltersCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-accent-foreground text-xs rounded-full flex items-center justify-center">
                      {activeFiltersCount}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-md">
                <SheetHeader>
                  <SheetTitle>Filtros</SheetTitle>
                </SheetHeader>
                
                <div className="mt-6 space-y-6">
                  {/* Dietary */}
                  <div>
                    <Label className="text-sm font-medium">Preferencias alimentarias</Label>
                    <div className="mt-3 space-y-3">
                      {dietaryTags.map(tag => (
                        <label 
                          key={tag.id}
                          className="flex items-center gap-3 cursor-pointer"
                        >
                          <Checkbox
                            checked={filters.dietary.includes(tag.id)}
                            onCheckedChange={() => toggleDietaryFilter(tag.id)}
                            data-testid={`filter-${tag.id}`}
                          />
                          <span className="text-sm">{tag.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Price Range */}
                  <div>
                    <Label className="text-sm font-medium">
                      Rango de precio: ${filters.priceRange[0]} - ${filters.priceRange[1]}
                    </Label>
                    <Slider
                      value={filters.priceRange}
                      onValueChange={(value) => setFilters(prev => ({ ...prev, priceRange: value }))}
                      max={300}
                      step={10}
                      className="mt-4"
                      data-testid="price-range-slider"
                    />
                  </div>

                  {/* Quick filters */}
                  <div>
                    <Label className="text-sm font-medium">Filtros rápidos</Label>
                    <div className="mt-3 space-y-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <Checkbox
                          checked={filters.spicy}
                          onCheckedChange={(checked) => setFilters(prev => ({ ...prev, spicy: checked }))}
                          data-testid="filter-spicy"
                        />
                        <span className="text-sm flex items-center gap-1">
                          <Flame className="w-4 h-4 text-orange-500" />
                          Solo picantes
                        </span>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <Checkbox
                          checked={filters.popular}
                          onCheckedChange={(checked) => setFilters(prev => ({ ...prev, popular: checked }))}
                          data-testid="filter-popular"
                        />
                        <span className="text-sm">Solo más vendidos</span>
                      </label>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-4 border-t border-border">
                    <Button 
                      variant="outline" 
                      className="flex-1 rounded-full"
                      onClick={clearFilters}
                      data-testid="clear-filters-btn"
                    >
                      Limpiar
                    </Button>
                    <Button 
                      className="flex-1 rounded-full"
                      onClick={() => setFiltersOpen(false)}
                      data-testid="apply-filters-btn"
                    >
                      Aplicar
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="sticky top-16 md:top-20 z-40 bg-background/80 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Tabs value={activeCategory} onValueChange={setActiveCategory} className="w-full">
            <TabsList className="h-auto p-0 bg-transparent justify-start gap-1 overflow-x-auto hide-scrollbar flex-nowrap w-full">
              {categories.map(cat => (
                <TabsTrigger
                  key={cat.id}
                  value={cat.id}
                  className="px-4 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-accent data-[state=active]:bg-transparent whitespace-nowrap"
                  data-testid={`category-tab-${cat.id}`}
                >
                  {cat.icon && <span className="mr-1">{cat.icon}</span>}
                  {cat.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Active Filters Display */}
      {(activeFiltersCount > 0 || searchQuery) && (
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">Filtros activos:</span>
            
            {searchQuery && (
              <Badge variant="secondary" className="gap-1">
                Búsqueda: {searchQuery}
                <button onClick={() => setSearchQuery('')}>
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            
            {filters.dietary.map(diet => (
              <Badge key={diet} variant="secondary" className="gap-1">
                {dietaryTags.find(d => d.id === diet)?.label}
                <button onClick={() => toggleDietaryFilter(diet)}>
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
            
            {filters.spicy && (
              <Badge variant="secondary" className="gap-1">
                Picante
                <button onClick={() => setFilters(prev => ({ ...prev, spicy: false }))}>
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            
            {filters.popular && (
              <Badge variant="secondary" className="gap-1">
                Popular
                <button onClick={() => setFilters(prev => ({ ...prev, popular: false }))}>
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            
            {(filters.priceRange[0] > 0 || filters.priceRange[1] < 300) && (
              <Badge variant="secondary" className="gap-1">
                ${filters.priceRange[0]} - ${filters.priceRange[1]}
                <button onClick={() => setFilters(prev => ({ ...prev, priceRange: [0, 300] }))}>
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            
            <button 
              onClick={clearFilters}
              className="text-sm text-accent hover:underline ml-2"
            >
              Limpiar todo
            </button>
          </div>
        </div>
      )}

      {/* Products Grid */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-muted-foreground">
            {filteredProducts.length} {filteredProducts.length === 1 ? 'producto' : 'productos'}
          </p>
        </div>

        <AnimatePresence mode="wait">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array(8).fill(0).map((_, i) => <ProductCardSkeleton key={i} />)}
            </div>
          ) : filteredProducts.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-10 h-10 text-muted-foreground" />
              </div>
              <h3 className="font-medium text-lg">No encontramos resultados</h3>
              <p className="text-muted-foreground mt-2">
                Intenta con otros filtros o busca algo diferente
              </p>
              <Button 
                variant="outline" 
                className="mt-4 rounded-full"
                onClick={clearFilters}
              >
                Limpiar filtros
              </Button>
            </motion.div>
          ) : (
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {filteredProducts.map((product, index) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  index={index}
                  onViewDetails={setSelectedProduct}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Product Detail Modal */}
      <ProductDetail
        product={selectedProduct}
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
      />
    </div>
  );
}
